"use client"

// ds-typo-ignore-file — phone mockups simulate the get.aheadhealth.com quiz UI (intentional sub-DS sizes), not marketing-site typography
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { ArrowRightIcon, Button } from "@/components/ui/button"
import { QuizCtaLink } from "@/components/quiz/quiz-cta-link"
import { useCountry } from "@/components/layout/country-provider"
import { useSectionGate } from "@/components/layout/section-visibility-provider"
import { isMembershipFirstSite } from "@/lib/membership-first"
import { MembershipFirstQuizCtaBanner } from "@/components/marketing/quiz-cta-banner-membership-first"

/**
 * Quiz CTA banner — drives traffic to the personalised check-up quiz.
 * Links to the in-site /quiz-focus funnel (see QuizCtaLink). White surface,
 * copy + text-link CTA on the left, two upright phone mockups of the live
 * quiz clipped on the bottom-right.
 * Headline + CTA + subtext come from the `ui` namespace.
 */

/**
 * `variant`:
 *   - "default" — full banner: headline + subtext + CTA, full-size phones.
 *   - "compact" — ~40% shorter: headline + CTA only (no subtext), slightly
 *     smaller headline, and the phones scaled down to fit the lower height.
 */
export function QuizCtaBanner({
  variant = "default",
}: {
  variant?: "default" | "compact"
}) {
  const compact = variant === "compact"
  const { code: country } = useCountry()
  const isAllowed = useSectionGate()
  const t = useTranslations("ui")
  const HEADLINE = t("quizCtaHeadline")
  const SUBTEXT = t("quizCtaSubtext")
  const CTA_LABEL = t("quizCtaCta")
  // CH + NL. Headline/subtext/CTA and — since the mobile audit found Dutch and
  // German headlines sitting on English answer labels — the phone mockups below
  // are translated per locale. The mockups still depict the retired 3-step
  // external quiz rather than the live /quiz-focus funnel; AHD-9270 redraws
  // them. Gate registry: section:marketing.quiz-cta-banner.
  if (!isAllowed("section:marketing.quiz-cta-banner", country)) return null
  // The membership-first site has its own banner (photographed phones, a
  // filled CTA and a contact button); this one stays as it is until launch.
  if (isMembershipFirstSite(country))
    return <MembershipFirstQuizCtaBanner variant={variant} />
  return (
    <section
      data-testid="quiz-cta-banner"
      className="relative overflow-hidden rounded-[16px] bg-white"
    >
      <div
        className={
          // Mobile: centred horizontally. md+: left-aligned. Both:
          // vertically centred in the column.
          "relative z-10 flex flex-col items-center justify-center px-8 text-center md:max-w-[58%] md:items-start md:px-14 md:text-left " +
          // Big mobile bottom padding reserves the zone the mobile phones
          // peek into; md+ reverts to the normal vertical rhythm.
          (compact
            ? "gap-4 pt-7 pb-[210px] md:py-9"
            : "gap-3 pt-12 pb-[210px] md:py-16")
        }
      >
        <h2
          className={
            "text-neutral-900 " +
            (compact
              ? "type-headline-sm md:whitespace-nowrap"
              : "type-headline-lg max-w-[22ch]")
          }
        >
          {HEADLINE}
        </h2>
        {!compact && (
          <p className="type-body-md max-w-[42ch] text-neutral-500">
            {SUBTEXT}
          </p>
        )}
        <div>
          <QuizCtaLink>
            {/* DS text-variant button — no fill. `gradient-ring` is the
                only visible outline (conic-gradient ring, visible by
                default, animates on hover). Same ring + animation as
                the homepage "New: Ahead membership" pill. */}
            <Button
              variant="text"
              className="group gradient-ring"
              iconRight={
                <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
              }
            >
              {CTA_LABEL}
            </Button>
          </QuizCtaLink>
        </div>
      </div>

      {/* Phone mockups — desktop only, clipped by the section's overflow.
          The whole phone region is a link to the quiz; the mockups
          themselves stay decorative (aria-hidden). */}
      <QuizCtaLink
        aria-label={CTA_LABEL}
        className="absolute top-0 right-0 hidden h-full w-[55%] md:block"
      >
        <div aria-hidden="true">
          {compact ? (
            <>
              {/* Back phone — scaled down, higher and further right */}
              <div className="absolute" style={{ top: "52px", right: "76px" }}>
                <QuizPhone screen="about" scale={0.7} />
              </div>
              {/* Front phone — scaled down, slightly to the left */}
              <div className="absolute" style={{ top: "24px", right: "212px" }}>
                <QuizPhone screen="question" scale={0.7} />
              </div>
            </>
          ) : (
            <>
              {/* Back phone — higher and further right */}
              <div
                className="absolute"
                style={{ top: "100px", right: "110px" }}
              >
                <QuizPhone screen="about" />
              </div>
              {/* Front phone — slightly to the left */}
              <div className="absolute" style={{ top: "60px", right: "270px" }}>
                <QuizPhone screen="question" />
              </div>
            </>
          )}
        </div>
      </QuizCtaLink>

      {/* Phone mockups — mobile only. Sit below the title + button in the
          reserved bottom padding zone, absolutely positioned so only the
          top ~⅔ shows (the rest bleeds past the section's overflow). */}
      <QuizCtaLink
        aria-label={CTA_LABEL}
        className="absolute inset-x-0 bottom-0 h-[210px] md:hidden"
      >
        {/* Inner wrapper is exactly the width of the two-phone cluster
            (224px) and centred, so the phones sit in the middle of the
            banner rather than anchored to the right edge. */}
        <div
          aria-hidden="true"
          className="absolute top-0 left-1/2 h-full w-[224px] -translate-x-1/2"
        >
          {/* Back phone — rightmost in the cluster */}
          <div className="absolute" style={{ top: "58px", right: "0px" }}>
            <QuizPhone screen="about" scale={0.6} />
          </div>
          {/* Front phone — higher and further left */}
          <div className="absolute" style={{ top: "32px", right: "92px" }}>
            <QuizPhone screen="question" scale={0.6} />
          </div>
        </div>
      </QuizCtaLink>
    </section>
  )
}

/** Stylised Ahead arrow — from `public/icons/ahead-logo.svg`. */
function AheadArrow({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 15 16"
      className={className}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M11.3742 0H0V1.99316L11.2938 1.71601C12.3158 1.69133 13.1522 2.51707 13.1311 3.53073L12.8861 15.7934H14.8593V3.4586C14.8593 1.54897 13.2976 0 11.3723 0H11.3742Z" />
      <path d="M9.37226 4.30513L0 14.3525L1.42584 15.8028L10.8517 5.66047C11.2325 5.25045 11.2019 4.61264 10.7828 4.24059C10.3732 3.87612 9.74546 3.9046 9.37226 4.30513Z" />
    </svg>
  )
}

/**
 * Inline phone mockup recreating a screen from the live get.aheadhealth.com
 * quiz. Decorative — content mirrors the real quiz but isn't pixel-legible.
 *   - "question": step 1 — "What would you like to focus on?"
 *   - "about": step 2 — "A bit about you" (age + gender).
 */
function QuizPhone({
  screen = "question",
  scale = 1,
}: {
  screen?: "question" | "about"
  scale?: number
}) {
  const step = screen === "question" ? 1 : 2
  const t = useTranslations("ui")
  const locale = useLocale()
  return (
    <div
      className="relative h-[420px] w-[220px] overflow-hidden rounded-[32px] border-[6px] border-neutral-900 bg-white shadow-[0_18px_36px_-18px_rgba(45,45,45,0.45)]"
      style={{ transform: `scale(${scale})`, transformOrigin: "top right" }}
    >
      <div className="absolute top-1.5 left-1/2 z-10 h-3 w-16 -translate-x-1/2 rounded-full bg-neutral-900" />
      <div className="flex flex-col items-center px-4 pt-7 pb-3">
        <AheadArrow className="text-aubergine-800 h-3.5 w-3.5" />
        <div className="mt-2 flex w-full items-center justify-between">
          <span className="type-label-sm text-neutral-500">
            {t("quizPhoneStep", { step: String(step) })}
          </span>
          {/* The badge mimics the quiz's own language chip; it has to follow
            the language the mockup is now drawn in. */}
          <span className="type-label-sm text-neutral-400">
            {locale.toUpperCase()}
          </span>
        </div>
        <div className="mt-2 flex w-full gap-1">
          <span className="bg-aubergine-800 h-1 flex-1 rounded-full" />
          <span
            className={
              "h-1 flex-1 rounded-full " +
              (step >= 2 ? "bg-aubergine-800" : "bg-neutral-200")
            }
          />
          <span className="h-1 flex-1 rounded-full bg-neutral-200" />
        </div>
      </div>
      {screen === "question" ? <QuestionScreen /> : <AboutScreen />}
    </div>
  )
}

function QuestionScreen() {
  const t = useTranslations("ui")
  const options = [
    { id: "general", label: t("quizPhoneFocusGeneral"), checked: true },
    { id: "heart", label: t("quizPhoneFocusHeart"), checked: false },
    { id: "hormones", label: t("quizPhoneFocusHormones"), checked: false },
    { id: "brain", label: t("quizPhoneFocusBrain"), checked: false },
  ]
  return (
    <div className="flex h-full flex-col px-4 pb-4">
      <h3
        className="mt-3 text-center text-[13px] font-medium text-neutral-900"
        // ds-typo-ignore: inline phone-screen mockup — not page typography
      >
        {t("quizPhoneQuestionTitle")}
      </h3>
      <p
        className="mt-0.5 text-center text-[10px] text-neutral-400"
        // ds-typo-ignore
      >
        {t("quizPhoneSelectAll")}
      </p>
      <ul className="mt-3 flex flex-col gap-1.5">
        {options.map((o) => (
          <li
            key={o.id}
            className="flex items-center gap-2 rounded-[10px] bg-neutral-50 px-2.5 py-2"
          >
            <span
              className={
                "flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[3px] border " +
                (o.checked
                  ? "border-aubergine-800 bg-aubergine-800 text-white"
                  : "border-neutral-300 bg-white")
              }
            >
              {o.checked && (
                <svg
                  viewBox="0 0 10 10"
                  className="h-2 w-2"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M2 5l2 2 4-4"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </span>
            <span
              className="text-[10px] font-medium text-neutral-900"
              // ds-typo-ignore
            >
              {o.label}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-auto flex justify-end pt-2">
        <span
          className="bg-aubergine-800 rounded-full px-3 py-1.5 text-[10px] font-medium text-white"
          // ds-typo-ignore
        >
          {t("quizPhoneContinue")}
        </span>
      </div>
    </div>
  )
}

/** Step 2 of the live quiz — "A bit about you" (age range + gender). */
function AboutScreen() {
  const t = useTranslations("ui")
  const ageRanges = [
    { id: "under40", label: t("quizPhoneAgeUnder40"), on: false },
    { id: "40to59", label: t("quizPhoneAge40to59"), on: true },
    { id: "60to69", label: t("quizPhoneAge60to69"), on: false },
    { id: "70plus", label: t("quizPhoneAge70plus"), on: false },
  ]
  const genders = [
    { id: "male", label: t("quizPhoneMale"), on: true },
    { id: "female", label: t("quizPhoneFemale"), on: false },
  ]
  const pill = (on: boolean) =>
    "rounded-[10px] border px-2 py-2 text-center text-[10px] font-medium " +
    (on
      ? "border-aubergine-800 bg-aubergine-50 text-aubergine-900"
      : "border-neutral-200 bg-neutral-50 text-neutral-700")
  return (
    <div className="flex h-full flex-col px-4 pb-4">
      <h3
        className="mt-3 text-center text-[13px] font-medium text-neutral-900"
        // ds-typo-ignore: inline phone-screen mockup — not page typography
      >
        {t("quizPhoneAboutTitle")}
      </h3>
      <p
        className="mt-3 text-[10px] font-medium text-neutral-500"
        // ds-typo-ignore
      >
        {t("quizPhoneAgeRange")}
      </p>
      <div className="mt-1.5 grid grid-cols-2 gap-1.5">
        {ageRanges.map((o) => (
          <span key={o.id} className={pill(o.on)}>
            {o.label}
          </span>
        ))}
      </div>
      <p
        className="mt-3 text-[10px] font-medium text-neutral-500"
        // ds-typo-ignore
      >
        {t("quizPhoneGenderAtBirth")}
      </p>
      <div className="mt-1.5 grid grid-cols-2 gap-1.5">
        {genders.map((o) => (
          <span key={o.id} className={pill(o.on)}>
            {o.label}
          </span>
        ))}
      </div>
      <div className="mt-auto flex justify-end pt-2">
        <span
          className="bg-aubergine-800 rounded-full px-3 py-1.5 text-[10px] font-medium text-white"
          // ds-typo-ignore
        >
          {t("quizPhoneContinue")}
        </span>
      </div>
    </div>
  )
}
